import { useMemo } from 'react';

import { BaseDto } from '@/shared/lib/sj-orm/models/base.dto';
import { Table, TableProps } from '@/shared/uikitV2/Table';

import { useCaseColumn } from './hooks/useCaseColumn';
import { EmptyList } from './EmptyList';
import { DocumentsMap, EntityTableOptions } from './types';

type EntitiesTableProps<T extends BaseDto> = {
  options: EntityTableOptions<T>;
  selected: DocumentsMap;
  setSelected: (items: DocumentsMap) => void;
  showValidationErrors: boolean;
  defaultCaseId?: string;
};

export const EntitiesTable = <T extends BaseDto>({
  options,
  selected,
  setSelected,
  showValidationErrors,
  defaultCaseId,
}: EntitiesTableProps<T>) => {
  const caseColumn = useCaseColumn<T>({
    selected,
    setSelected,
    showValidationErrors,
    defaultCaseId,
  });

  const columns = useMemo(() => [...options.columns, caseColumn], [options.columns, caseColumn]);

  const rowSelection: TableProps<T>['rowSelection'] = {
    type: 'checkbox',
    selectedRowKeys: Array.from(selected.keys()),
    columnWidth: 42,
    onChange: (keys) => {
      const next = new Map(selected);
      options.data.forEach((item) => next.delete(item.id));
      keys.forEach((key) => {
        const id = String(key);
        next.set(id, selected.get(id) ?? defaultCaseId ?? null);
      });
      setSelected(next);
    },
  };

  if (!options.data.length) {
    return <EmptyList />;
  }

  return <Table<T> columns={columns} items={options.data} rowSelection={rowSelection} />;
};
